/**
 * DailyQuest widget -- Today's quest with progress bar and XP reward.
 */

import type { Widget, WidgetContext } from '../types';

const LS_QUEST_KEY = 'aweni_daily_quest';

interface Quest {
  text: string;
  target: number;
  current: number;
  xp: number;
  module: string;
  date: string;
}

function getTodayQuest(t: (k: string, p?: Record<string, string | number>) => string): Quest {
  const today = new Date().toDateString();
  try {
    const raw = localStorage.getItem(LS_QUEST_KEY);
    if (raw) {
      const q = JSON.parse(raw) as Quest;
      if (q.date === today) return q;
    }
  } catch { /* ignore */ }

  // Generate a new daily quest based on day-of-year
  const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0).getTime()) / 86400000);
  const quests = [
    { text: t('quest_play_math'), target: 3, xp: 50, module: 'games' },
    { text: t('quest_review_cards'), target: 10, xp: 40, module: 'memoriz' },
    { text: t('quest_read_articles'), target: 2, xp: 30, module: 'blog' },
    { text: t('quest_play_games'), target: 5, xp: 60, module: 'games' },
    { text: t('quest_complete_challenge'), target: 1, xp: 75, module: 'community' },
    { text: t('quest_review_20'), target: 20, xp: 80, module: 'memoriz' },
    { text: t('quest_explore_new'), target: 2, xp: 35, module: 'games' },
  ];
  const selected = quests[dayOfYear % quests.length];
  const quest: Quest = { ...selected, current: 0, date: today };
  try { localStorage.setItem(LS_QUEST_KEY, JSON.stringify(quest)); } catch { /* */ }
  return quest;
}

export const DailyQuest: Widget = {
  id: 'daily-quest',
  label: { en: 'Daily Quest', fr: 'Quete du jour', ar: 'مهمة اليوم', es: 'Mision diaria', de: 'Tagesquest' },
  icon: '🎯',
  defaultVisible: true,
  defaultOrder: 4,
  defaultSize: 'compact',

  render(container: HTMLElement, size: 'compact' | 'expanded', ctx: WidgetContext): void {
    const quest = getTodayQuest(ctx.t);
    const progress = Math.min(100, Math.round((quest.current / quest.target) * 100));
    const completed = quest.current >= quest.target;

    if (size === 'compact') {
      // 0-state: render a friendly CTA instead of a "0/10" progress bar
      // (progress bars at 0 read as "failed" — NN/g + audit recommendation
      // 2026-04-30). Once the user makes progress, switch to the bar.
      if (quest.current === 0) {
        container.innerHTML = `
          <div class="rounded-2xl p-4 cursor-pointer" style="background:var(--card-bg);border:1px solid var(--border);" data-nav="${quest.module}">
            <div class="flex items-center justify-between gap-3">
              <div class="flex items-center gap-2 min-w-0">
                <span style="font-size:18px;flex-shrink:0;">🎯</span>
                <div class="min-w-0">
                  <div class="text-sm font-semibold" style="color:var(--text-primary);">${quest.text}</div>
                  <div class="text-xs" style="color:var(--text-secondary);">${ctx.t('home_quest_start_cta') || 'Start now'}</div>
                </div>
              </div>
              <span class="text-xs font-bold px-2.5 py-1 rounded-full" style="background:#F59E0B1A;color:#F59E0B;flex-shrink:0;">+${quest.xp} XP</span>
            </div>
          </div>`;
      } else {
        container.innerHTML = `
          <div class="rounded-2xl p-4 cursor-pointer" style="background:var(--card-bg);border:1px solid var(--border);" data-nav="${quest.module}">
            <div class="flex items-center justify-between mb-2">
              <div class="flex items-center gap-2">
                <span>🎯</span>
                <span class="text-sm font-medium" style="color:var(--text-primary);">${quest.text}</span>
              </div>
              <span class="text-xs font-bold" style="color:#F59E0B;">+${quest.xp} XP</span>
            </div>
            <div class="flex items-center gap-2">
              <div class="flex-1 rounded-full overflow-hidden" style="height:6px;background:var(--bg-secondary);">
                <div class="rounded-full transition-all" style="height:100%;width:${progress}%;background:${completed ? '#0A0A0A' : '#3B82F6'};"></div>
              </div>
              <span class="text-xs font-bold" style="color:${completed ? '#0A0A0A' : 'var(--text-secondary)'};">${quest.current}/${quest.target}</span>
            </div>
          </div>`;
      }
    } else {
      container.innerHTML = `
        <div class="rounded-2xl p-5 cursor-pointer" style="background:var(--card-bg);box-shadow:var(--shadow-sm);" data-nav="${quest.module}">
          <div class="flex items-center justify-between mb-3">
            <h3 class="text-sm font-semibold uppercase tracking-wide" style="color:var(--text-secondary);">${ctx.t('widget_daily_quest')}</h3>
            <span class="text-xs font-bold px-2 py-1 rounded-full" style="background:#F59E0B20;color:#F59E0B;">+${quest.xp} XP</span>
          </div>
          <p class="text-base font-medium mb-3" style="color:var(--text-primary);">🎯 ${quest.text}</p>
          <div class="flex items-center gap-3">
            <div class="flex-1 rounded-full overflow-hidden" style="height:8px;background:var(--bg-secondary);">
              <div class="rounded-full transition-all" style="height:100%;width:${progress}%;background:${completed ? '#0A0A0A' : '#3B82F6'};"></div>
            </div>
            <span class="text-sm font-bold" style="color:${completed ? '#0A0A0A' : 'var(--text-secondary)'};">
              ${completed ? '✅' : `${quest.current}/${quest.target}`}
            </span>
          </div>
        </div>`;
    }

    container.querySelectorAll<HTMLElement>('[data-nav]').forEach((el) => {
      el.addEventListener('click', () => ctx.navigate(quest.module));
    });
  },
};
